"use client";

import { useRef, useEffect, type ReactNode } from "react";
import { AssistantRuntimeProvider, ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import axios from "axios";
import { logger } from "@/helpers/tslog";
import { getToken } from "@/utils/localstorage";
import { useContentSlideStore, type SlideFrameKey } from "@/state/contentSlideStore";
import { useSlidesDialogStore, type SlideFrameId } from "@/state/slidesDialogStore";

const INIT_SENTINEL = "__INIT_TimePeriodBenchmarkStep__";
const STREAM_CHAR_DELAY_MS = 4;

function slideFrameKeyToId(key: SlideFrameKey): SlideFrameId {
  const n = Number(key.replace(/^slideFrame/, ""));
  if (!Number.isInteger(n) || n < 1 || n > 9) {
    throw new Error(`Unexpected slide frame key: ${key}`);
  }
  return n as SlideFrameId;
}

type CurrentUserResponse = {
  id: string;
  email: string;
  fullName: string;
  conversationId: string | null;
};

async function fetchUserFirstName(): Promise<string> {
  const apiUrl = process.env.API_URL;
  if (!apiUrl) return "there";
  try {
    const token = await getToken();
    const { data } = await axios.get<CurrentUserResponse>(`${apiUrl}/user/current-user`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    const first = data?.fullName?.trim().split(/\s+/)[0];
    return first || "there";
  } catch (e) {
    logger.warn("ContentSlideAlphaCreateRuntime failed to load current user", e);
    return "there";
  }
}

const ContentSlideAlphaCreateAdapter: ChatModelAdapter = {
  async *run({ messages }): AsyncGenerator<
    { content: Array<{ type: "text"; text: string }> },
    void,
    unknown
  > {
    const lastMessage = messages[messages.length - 1];
    let userInput = "";

    if (lastMessage?.content && Array.isArray(lastMessage.content)) {
      const textPart = lastMessage.content.find(
        (part) => typeof part === "object" && "text" in part && typeof part.text === "string"
      );
      if (textPart) userInput = (textPart as { text: string }).text;
    }
    const isInitialization = userInput === INIT_SENTINEL;

    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch {
      // ignore
    }

    let response: string;

    if (isInitialization) {
      try {
        const { primaryCompany, selectedSlideFrameKey } = useContentSlideStore.getState();
        if (!selectedSlideFrameKey) {
          throw new Error("No slide frame selected.");
        }
        const slideFrameId = slideFrameKeyToId(selectedSlideFrameKey);
        const userFirstName = await fetchUserFirstName();
        response = await useSlidesDialogStore.getState().initialize(
          primaryCompany?.companyTicker ?? "",
          primaryCompany?.companyExchange ?? "",
          primaryCompany?.companyName ?? "",
          [slideFrameId],
          userFirstName
        );
      } catch (err) {
        logger.error("ContentSlideAlphaCreateRuntime initialize failed", err);
        response = "Sorry, I was unable to start the dialog. Please try again.";
      }
    } else if (userInput) {
      try {
        response = await useSlidesDialogStore.getState().interact(userInput);
      } catch (err) {
        logger.error("ContentSlideAlphaCreateRuntime interact failed", err);
        response = "Sorry, something went wrong processing your message. Please try again.";
      }
    } else {
      response = "Thanks for your reply. Press **Next** when you are ready to continue.";
    }

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    for (let i = 1; i <= response.length; i++) {
      if (i === 1) {
        try {
          window.dispatchEvent(new CustomEvent("ai-first-token"));
        } catch {
          // ignore
        }
      }
      yield {
        content: [{ type: "text", text: response.slice(0, i) }],
      };
      await delay(STREAM_CHAR_DELAY_MS);
    }

    try {
      window.dispatchEvent(new CustomEvent("ai-run-end"));
    } catch {
      // ignore
    }
  },
};

export function ContentSlideAlphaCreateRuntime({ children }: { children: ReactNode }) {
  const runtime = useLocalRuntime(ContentSlideAlphaCreateAdapter, {
    initialMessages: [],
  });

  const initSentRef = useRef(false);
  useEffect(() => {
    if (initSentRef.current) return;

    try {
      localStorage.removeItem("messages");
    } catch (e) {
      logger.warn("Failed to clear messages cache", e);
    }

    try {
      const thread = (runtime as { thread?: { append: (m: unknown) => void } })?.thread;
      if (thread && typeof thread.append === "function") {
        thread.append({
          role: "system",
          content: [{ type: "text", text: INIT_SENTINEL }],
          startRun: true,
        });
        initSentRef.current = true;
      }
    } catch {
      // ignore
    }
  }, [runtime]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
